import { useContext } from 'react'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { useTranslation } from 'libs/i18n'
import { NavLink } from 'libs/router'
import { ContextMenu, Currency, PayoutDestination } from 'shared/components'
import { Badge, Detail } from 'shared/atoms'
import { StoreSettingsContext } from 'store'
import { routes } from 'config/routes'
import type { Option } from 'shared/components/ContextMenu/types'
import type { UserListItem } from '../../types/users.types'

export type UserProps = {
	user: UserListItem
	menuOptions: Option[]
}

export const User = ({ user, menuOptions }: UserProps) => {
	const { t } = useTranslation()
	const storeCtx = useContext(StoreSettingsContext)
	const currency = storeCtx?.storeSettings?.currency
	const {
		id,
		name,
		email,
		phone_number,
		current_bookings,
		profit,
		outstanding_debts,
		balance,
		has_payout_request,
		default_payout_method
	} = user

	return (
		<li className="p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-[13px] font-semibold text-foreground">{name || '—'}</p>
					<p className="truncate text-[11px] text-muted-foreground">{email}</p>
				</div>
				<div className="flex shrink-0 items-center gap-1">
					{has_payout_request && (
						<Badge variant="warning" className="px-2.5 text-[11px]">
							{t('Requested')}
						</Badge>
					)}
					<ContextMenu
						className="-mr-2"
						dataId={id}
						item={user}
						options={menuOptions}
						label={t('Actions for {{ name }}', { name: name || email })}
					/>
				</div>
			</div>
			<dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
				<Detail
					label={t('Reference')}
					value={
						<NavLink
							className="font-semibold text-primary hover:underline"
							to={routes.userEdit(String(id))}>
							#{id}
						</NavLink>
					}
				/>
				<Detail
					label={t('Phone')}
					value={phone_number ? formatPhoneNumberIntl(phone_number) : '—'}
				/>
				<Detail
					label={t('Payout method')}
					value={<PayoutDestination destination={default_payout_method} />}
				/>
				<Detail
					label={t('Current bookings')}
					value={
						current_bookings?.length
							? current_bookings.map((booking) => `#${booking.ref_nr}`).join(', ')
							: '—'
					}
				/>
				<Detail label={t('Total')} value={<Currency price={{ amount: profit, currency }} />} />
				<Detail
					label={t('Outstanding debt')}
					value={<Currency price={{ amount: outstanding_debts, currency }} />}
				/>
				<Detail
					className="col-span-2"
					label={t('Balance')}
					value={
						<Currency
							className="font-display text-[15px] font-bold"
							price={{ amount: balance, currency }}
						/>
					}
				/>
			</dl>
		</li>
	)
}
